import { useEffect, useMemo, useState } from 'react';

import { useForm } from '@mantine/form';

import { useRegistryCompanySearch } from '@/hooks/useRegistryCatalogue';
import type { RegistryOrderSubject } from '@/hooks/useRegistryOrderSubjects';
import type { RegistryCompanyCandidate } from '@/types/registry-documents';
import {
  EMPTY_MANUAL,
  manualValuesFor,
  type CompanyMode,
  type ManualFormValues,
} from './orderDraft';

interface UseOrderCompanyOptions {
  opened: boolean;
  reportId: string;
  subjects: RegistryOrderSubject[];
}

/**
 * The company step: which subject and country, and the company itself —
 * picked from a search of Armadillo's index or described by hand.
 */
export function useOrderCompany({ opened, reportId, subjects }: UseOrderCompanyOptions) {
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [mode, setMode] = useState<CompanyMode>('search');
  const [searchName, setSearchName] = useState('');
  const [searchRegistrationNumber, setSearchRegistrationNumber] = useState('');
  const [candidate, setCandidate] = useState<RegistryCompanyCandidate | null>(null);
  const [prefilledFrom, setPrefilledFrom] = useState<string | null>(null);

  const manual = useForm<ManualFormValues>({
    mode: 'controlled',
    initialValues: EMPTY_MANUAL,
    validate: {
      companyName: (value) => (value.trim() ? null : 'Company name is required'),
    },
  });
  const search = useRegistryCompanySearch();

  const subject = useMemo(
    () => subjects.find((entry) => entry.id === subjectId) ?? null,
    [subjects, subjectId]
  );

  // Cleared on close so reopening never inherits a previous selection.
  useEffect(() => {
    if (!opened) {
      setSubjectId(null);
      setCountry(null);
      setMode('search');
      setSearchName('');
      setSearchRegistrationNumber('');
      setCandidate(null);
      setPrefilledFrom(null);
      manual.reset();
      search.reset();
    }
  }, [opened]);

  // Default to the first company subject once the list arrives — but only
  // while the form is untouched. Subjects can land after the analyst has
  // started typing, and a default then would wipe their work.
  const untouched = [
    country === null,
    searchName === '',
    searchRegistrationNumber === '',
    candidate === null,
    mode === 'search',
    !manual.isDirty(),
  ].every(Boolean);
  const neverPrefilled = subjectId === null && prefilledFrom === null;
  const firstSubjectId = subjects[0]?.id ?? null;
  useEffect(() => {
    if ([opened, untouched, neverPrefilled, firstSubjectId !== null].every(Boolean)) {
      setSubjectId(firstSubjectId);
    }
  }, [opened, untouched, neverPrefilled, firstSubjectId]);

  // A (new) subject prefills the search and the manual form; the analyst's
  // own edits are kept when the same subject is re-selected.
  useEffect(() => {
    if (!subject || subject.id === prefilledFrom) {
      return;
    }
    setPrefilledFrom(subject.id);
    setCountry(subject.jurisdictions[0]?.code ?? null);
    setSearchName(subject.name);
    setSearchRegistrationNumber(subject.registrationNumber ?? '');
    // Dropped here as well as on a country change: two subjects can share a
    // country, and the first one's company must not ride along.
    setCandidate(null);
    search.reset();
    manual.setValues(manualValuesFor(subject));
  }, [subject, prefilledFrom]);

  // The index is per country: a change drops the picked company and its
  // results, or an order could carry one country's DUNS with another
  // country's documents.
  useEffect(() => {
    setCandidate(null);
    search.reset();
  }, [country]);

  const hasCriteria = searchName.trim().length > 0 || searchRegistrationNumber.trim().length > 0;

  const runSearch = () => {
    if (!country) {
      return;
    }
    setCandidate(null);
    void search.search({
      name: searchName.trim() || undefined,
      registration_number: searchRegistrationNumber.trim() || undefined,
      country,
      report_id: reportId,
    });
  };

  const named = mode === 'search' ? candidate !== null : manual.values.companyName.trim() !== '';

  return {
    /** The selected subject, when it is in the current list. */
    subject,
    subjectId,
    setSubjectId,
    country,
    setCountry,
    mode,
    setMode,
    searchName,
    setSearchName,
    searchRegistrationNumber,
    setSearchRegistrationNumber,
    candidate,
    setCandidate,
    manual,
    search,
    canSearch: Boolean(country) && hasCriteria,
    runSearch,
    /** A country and a company are both settled — the step can be left. */
    chosen: Boolean(country) && named,
    /** Whether the manual form may be left (always true in search mode). */
    validate: () => mode === 'search' || !manual.validate().hasErrors,
  };
}

export type OrderCompany = ReturnType<typeof useOrderCompany>;
